import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { LandlordDetailUIModel } from '../../../shared/ui-models/landlord.ui-model';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../shared/components/core-ui/button/button.component';
import { getLandlordDetail } from '../../data.service';
import BankAccountCard from './components/bank-account-card/bank-account-card.component';

const CARD = 'rounded-2xl border border-gray-200 bg-white p-6 shadow-sm';
const CARD_TITLE = 'm-0 mb-5 text-2xl font-gilroy-bold text-neutral-500';
const FOCUS_RING =
  'focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

const AUDIT_ACTION_LABEL: { [action: string]: string } = {
  created: 'Bank account created',
  updated: 'Bank account updated'
};

const AUDIT_FIELD_LABEL: { [field: string]: string } = {
  bankName: 'Bank name',
  accountHolderName: 'Account holder',
  accountNumber: 'Account number',
  swiftBic: 'SWIFT / BIC',
  country: 'Country',
  currency: 'Currency'
};

function formatTimestamp(value: string): string {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const BackToLandlords: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`mb-6 inline-flex items-center gap-2 rounded text-lg font-gilroy-medium text-gray-600 hover:text-primary-600 ${FOCUS_RING}`}
  >
    <svg aria-hidden="true" viewBox="0 0 6 10" className="h-3 w-2 shrink-0">
      <path d="M6 0L0 5l6 5z" fill="currentColor" />
    </svg>
    Back to landlords
  </button>
);

export const LandlordDetailsContainer: React.FC = () => {
  const { landlordId } = useParams<{ landlordId: string }>();
  const history = useHistory();

  const [detail, setDetail] = useState<LandlordDetailUIModel | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [reloadToken, setReloadToken] = useState<number>(0);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setHasError(false);

    getLandlordDetail(landlordId).then((response) => {
      if (!isActive) {
        return;
      }
      if (isErrorInfo(response)) {
        setHasError(true);
        setDetail(null);
      } else {
        setDetail(response);
      }
      setIsLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [landlordId, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);
  const goToList = useCallback(() => history.push(APP_ROUTES.LANDLORD_MODULE.FULL_PATH), [history]);

  if (isLoading) {
    return (
      <div className="p-8 font-gilroy-regular" aria-busy={true}>
        <BackToLandlords onClick={goToList} />
        <div className="mb-8 h-10 w-80 animate-pulse rounded bg-gray-200 motion-reduce:animate-none" />
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="h-80 animate-pulse rounded-2xl bg-gray-100 motion-reduce:animate-none lg:col-span-2" />
          <div className="h-80 animate-pulse rounded-2xl bg-gray-100 motion-reduce:animate-none" />
          <div className="h-56 animate-pulse rounded-2xl bg-gray-100 motion-reduce:animate-none lg:col-span-3" />
        </div>
      </div>
    );
  }

  if (hasError || !detail) {
    return (
      <div className="p-8 font-gilroy-regular">
        <BackToLandlords onClick={goToList} />
        <div className={`${CARD} py-16 text-center`}>
          <p className="m-0 text-xl font-gilroy-medium text-neutral-500">
            This landlord could not be loaded.
          </p>
          <div className="mt-5">
            <Button type={ButtonType.default} size={ButtonSize.small} onClick={reload}>
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const identity = [
    { label: 'Reference', value: detail.landlordId, numeric: true },
    { label: 'Email', value: detail.email || '—', numeric: false },
    { label: 'Phone', value: detail.phone || '—', numeric: true }
  ];

  return (
    <div className="p-8 font-gilroy-regular">
      <BackToLandlords onClick={goToList} />

      <div className="mb-8">
        <div className="flex flex-wrap items-center gap-4">
          <h1 className="m-0 text-4xl font-recoleta font-semibold text-neutral-500">
            {detail.name || 'Unnamed landlord'}
          </h1>
          {!detail.bankAccount && (
            <span className="rounded-full bg-secondary-50 px-4 py-1.5 text-lg font-gilroy-bold text-secondary-700">
              Bank details missing
            </span>
          )}
        </div>

        <dl className="m-0 mt-5 flex flex-wrap gap-x-12 gap-y-4">
          {identity.map((item) => (
            <div key={item.label}>
              <dt className="text-lg font-gilroy-medium text-gray-600">{item.label}</dt>
              <dd
                className={`m-0 mt-1 text-xl font-gilroy-medium text-neutral-500 ${
                  item.numeric ? 'tabular-nums' : ''
                }`}
              >
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
        <div className="flex flex-col gap-6 lg:col-span-2">
          <BankAccountCard
            config={{ landlordId: detail.landlordId, bankAccount: detail.bankAccount }}
            eventHandlers={{ savedHandler: reload }}
          />

          <section className={CARD}>
            <h2 className={CARD_TITLE}>
              Properties and units
              <span className="ml-2 font-gilroy-medium text-gray-600 tabular-nums">
                {detail.units.length}
              </span>
            </h2>
            {detail.units.length === 0 ? (
              <p className="m-0 text-xl font-gilroy-medium text-gray-600">
                No units assigned to this landlord.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table
                  className="w-full min-w-[640px] table-fixed border-separate border-spacing-0 text-left"
                  aria-label="Properties and units"
                >
                  <colgroup>
                    <col className="w-[30%]" />
                    <col className="w-[32%]" />
                    <col className="w-[24%]" />
                    <col className="w-[14%]" />
                  </colgroup>
                  <thead>
                    <tr>
                      {['Unit', 'Property', 'Area', 'Share'].map((label) => (
                        <th
                          key={label}
                          scope="col"
                          className={`border-b border-gray-200 py-4 text-lg font-gilroy-bold text-neutral-500 ${
                            label === 'Share' ? 'text-right' : ''
                          }`}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.units.map((unit, index) => (
                      <tr
                        key={`${unit.id}-${index}`}
                        className="text-xl font-gilroy-medium text-neutral-500"
                      >
                        <th
                          scope="row"
                          className="border-b border-gray-200 py-5 pr-6 text-left font-gilroy-bold"
                        >
                          {unit.name || '\u2014'}
                        </th>
                        <td className="border-b border-gray-200 py-5 pr-6 text-gray-600">
                          {unit.propertyName || '\u2014'}
                        </td>
                        <td className="border-b border-gray-200 py-5 pr-6 text-gray-600">
                          {unit.areaName || '\u2014'}
                        </td>
                        <td className="border-b border-gray-200 py-5 text-right tabular-nums">
                          {unit.landlordShare || 0}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <section className={CARD}>
          <h2 className={CARD_TITLE}>Activity</h2>
          {detail.audit.length === 0 ? (
            <p className="m-0 text-xl font-gilroy-medium text-gray-600">No changes recorded yet.</p>
          ) : (
            <ol className="m-0 flex list-none flex-col gap-7 border-l border-gray-200 p-0 pl-6">
              {detail.audit.map((entry, index) => (
                <li key={`${entry.performedAt}-${index}`} className="relative">
                  <span
                    aria-hidden="true"
                    className={`absolute -left-[18px] top-2 h-2.5 w-2.5 rounded-full ring-4 ring-white ${
                      index === 0 ? 'bg-primary-500' : 'bg-gray-200'
                    }`}
                  />
                  <p className="m-0 text-xl font-gilroy-bold text-neutral-500">
                    {AUDIT_ACTION_LABEL[entry.action] || entry.action}
                    {entry.source === 'import' ? ' via import' : ''}
                  </p>
                  <p className="m-0 mt-1 text-lg font-gilroy-medium text-gray-600">
                    {entry.performedBy || 'Unknown'}
                    {formatTimestamp(entry.performedAt)
                      ? ` on ${formatTimestamp(entry.performedAt)}`
                      : ''}
                  </p>
                  {entry.changes.length > 0 && (
                    <ul className="m-0 mt-3 flex list-none flex-col gap-1.5 p-0">
                      {entry.changes.map((change, changeIndex) => (
                        <li
                          key={`${change.field}-${changeIndex}`}
                          className="text-lg font-gilroy-medium text-neutral-500"
                        >
                          <span className="text-gray-600">
                            {AUDIT_FIELD_LABEL[change.field] || change.field}
                          </span>{' '}
                          {change.oldValue || 'empty'}{' '}
                          <span aria-hidden="true" className="text-gray-600">
                            {'\u2192'}
                          </span>{' '}
                          <span className="font-gilroy-bold">{change.newValue}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
};

export default LandlordDetailsContainer;
